import React, { useCallback, useMemo, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import KendoActionsCell from '@components/Kendo/ActionsCell';
import { Dialog } from '@progress/kendo-react-dialogs';
import I18n, { translate } from '@utils/i18n';
import { editWorkOrderPath, workOrderPath } from 'routes';
import { Button } from '@ui';
import ColumnMenuContext from './ColumnMenuContext';

const namespace = 'features.work_orders.index.table.actions';

function ActionsCell({ dataItem, colSpan, className, ariaColumnIndex, style }) {
  const { screen, screenId } = useContext(ColumnMenuContext);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);

  const authToken = useMemo(() => document.querySelector('meta[name="csrf-token"]').getAttribute('content'), []);

  const toggleDeleteDialog = useCallback(() => setDeleteDialogVisible((prev) => !prev), []);

  const actionsList = useMemo(() => {
    const list = [];

    if (dataItem.viewPath) {
      list.push({
        title: translate('view', { namespace }),
        faClass: 'fa-eye',
        link: dataItem.viewPath,
        blank: true
      });
    }

    if (dataItem.downloadPath) {
      list.push({
        title: translate('download', { namespace }),
        faClass: 'fa-download',
        link: dataItem.downloadPath,
        blank: true
      });
    }

    list.push({
      title: translate('edit', { namespace }),
      faClass: 'fa-pen-to-square',
      link: editWorkOrderPath({ id: dataItem.id, _options: true })
    });

    if (dataItem.sendAndClosePath) {
      list.push({
        title: translate('send', { namespace }),
        faClass: 'fa-envelope',
        link: dataItem.sendAndClosePath
      });
    }

    if (dataItem.followPath) {
      list.push({
        title: translate('follow', { namespace }),
        faClass: 'fa-star',
        link: dataItem.followPath
      });
    }

    list.push({
      title: translate('delete', { namespace }),
      faClass: 'fa-trash-can',
      onClick: toggleDeleteDialog
    });

    return list;
  }, [dataItem, toggleDeleteDialog]);

  const deletePath = useMemo(() => {
    const pathOptions = { id: dataItem.id, _options: true };
    let redirectUrl = null;

    const baseByScreen = { account: '/accounts', building: '/buildings', inspection: '/inspections' };
    if (screen && screenId && baseByScreen[screen]) {
      redirectUrl = `${baseByScreen[screen]}/${screenId}${
        screen === 'account' ? '#work-orders' : '#work_orders'
      }`;
    }

    if (redirectUrl) {
      pathOptions.redirect_path = redirectUrl;
    }

    return workOrderPath(pathOptions);
  }, [dataItem.id, screen, screenId]);

  return (
    <>
      <KendoActionsCell
        colSpan={colSpan}
        className={className}
        ariaColumnIndex={ariaColumnIndex}
        style={style}
        actionsList={actionsList}
      />

      {deleteDialogVisible && (
        <Dialog title={I18n.t('generic.confirm')} className="qmb-dialog" onClose={toggleDeleteDialog}>
          <p>{translate('confirm_delete', { namespace })}</p>
          <div className="modal__footer pull-right" style={{ justifyContent: 'space-between' }}>
            <form action={deletePath} method="POST">
              <input type="hidden" value="delete" name="_method" />
              <input name="authenticity_token" type="hidden" value={authToken} />
              <Button onClick={toggleDeleteDialog}>{I18n.t('generic.cancel')}</Button>
              <Button type="submit" variant="primary">
                {I18n.t('generic.ok')}
              </Button>
            </form>
          </div>
        </Dialog>
      )}
    </>
  );
}

ActionsCell.propTypes = {
  ariaColumnIndex: PropTypes.number.isRequired,
  style: PropTypes.object,
  className: PropTypes.string,
  colSpan: PropTypes.number.isRequired,
  dataItem: PropTypes.shape({
    id: PropTypes.number.isRequired,
    pdfPath: PropTypes.string,
    downloadPath: PropTypes.string,
    viewPath: PropTypes.string,
    sendAndClosePath: PropTypes.string,
    followPath: PropTypes.string
  }).isRequired
};

ActionsCell.defaultProps = {
  style: null,
  className: ''
};

export default ActionsCell;
